import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/hooks';
import { useRef, useCallback, useMemo } from 'react';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const savedRef = useRef(false);
  
  // Memoize the localStorage operation to prevent unnecessary writes
  const saveLastRoute = useCallback(() => {
    if (user && location.pathname !== '/login' && !savedRef.current) {
      localStorage.setItem('lastRoute', location.pathname);
      savedRef.current = true;
    }
  }, [user, location.pathname]);

  // Only save route when user is authenticated
  if (user) {
    saveLastRoute();
  }

  // Memoize the loading spinner to prevent re-renders
  const loadingSpinner = useMemo(() => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  ), []);

  // Fast path: if we have a user, render children immediately
  if (user && !isLoading) {
    return <>{children}</>;
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return loadingSpinner;
  }

  // Redirect to login page with the return url (including query parameters and hash)
  // Use full URL to match what Consent page does
  const redirectUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} replace />;
}